import { useEffect } from 'react';
import { Play, X } from 'lucide-react';
import { TOURS } from '../tours';
import { useTour } from '../hooks/useTour';

const SHORTCUTS: Array<{ keys: string[]; desc: string }> = [
  { keys: ['⌘ K', 'Ctrl K'], desc: 'Open command palette' },
  { keys: ['g', 'h'], desc: 'Go to Home' },
  { keys: ['g', 'a'], desc: 'Go to All Apps' },
  { keys: ['?'], desc: 'Show this help' },
  { keys: ['Esc'], desc: 'Close any dialog' },
];

type Props = { open: boolean; onClose: () => void };

export const HelpOverlay = ({ open, onClose }: Props) => {
  const { start } = useTour();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Help"
    >
      <div
        className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-xl border border-line bg-bg-primary p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Help</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-11 w-11 place-items-center rounded text-text-tertiary hover:bg-bg-secondary hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
          Keyboard shortcuts
        </div>
        <ul className="space-y-2.5">
          {SHORTCUTS.map((s, i) => (
            <li key={i} className="flex items-center justify-between gap-4 text-sm">
              <span className="text-text-secondary">{s.desc}</span>
              <span className="flex shrink-0 items-center gap-1">
                {s.keys.map((k, j) => (
                  <kbd
                    key={j}
                    className="rounded border border-line bg-bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-text-primary"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>

        {TOURS.length > 0 && (
          <>
            <div className="mt-6 mb-3 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
              Take a tour
            </div>
            <ul className="space-y-1.5">
              {TOURS.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      start(t);
                    }}
                    className="flex w-full items-start gap-3 rounded-md p-2 text-left text-sm hover:bg-bg-secondary"
                  >
                    <Play className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-orange" aria-hidden="true" />
                    <div className="flex-1">
                      <div className="font-medium">{t.title}</div>
                      <div className="text-xs text-text-tertiary">{t.blurb}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
